import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import type { SegmentedTabsCustomization } from '@reflex/ui/components/shared/SegmentedTabs';
import { useLocalStorageState } from '@reflex/ui/hooks/useWebStorageState';

/**
 * How the user has arranged the agent view tab bar. Purely a client-side
 * presentation preference, in the same family as the session sort order and the
 * nav style: every layout reaches the same tabs, it only changes which ones sit
 * in the bar and in what order.
 *
 * - `order`: tab ids in the order the user dragged them into.
 * - `hidden`: tab ids taken out of the rail. At least one available view
 *   always stays pinned; unpinning the active tab switches to a neighbor.
 *
 * Both lists are stored globally rather than per agent: the bar is a toolbar,
 * and a toolbar that rearranges itself between sessions is not a toolbar.
 */
const AgentTabLayoutSchema = z.object({
  order: z.array(z.string()),
  hidden: z.array(z.string()),
});

export type AgentTabLayout = z.infer<typeof AgentTabLayoutSchema>;

export const DEFAULT_AGENT_TAB_LAYOUT: AgentTabLayout = { order: [], hidden: [] };

const STORAGE_KEY = 'agent-view-tab-layout';

const unique = (values: string[]): string[] => [...new Set(values)];

/**
 * Parse a persisted value back into a layout. localStorage is an untrusted
 * boundary (stale keys from older builds, manual edits, a half-written value),
 * so anything that doesn't match falls back to the default instead of leaking
 * through. Duplicates are collapsed: they would give one tab two slots.
 */
export function parseAgentTabLayout(value: unknown): AgentTabLayout {
  const parsed = AgentTabLayoutSchema.safeParse(value);
  if (!parsed.success) return DEFAULT_AGENT_TAB_LAYOUT;
  return { order: unique(parsed.data.order), hidden: unique(parsed.data.hidden) };
}

/**
 * Apply a stored order to the views this agent actually has right now.
 *
 * The stored order is advisory, not authoritative: plugins come and go, the
 * preview tab appears only while a dev server is running, and a layout saved
 * months ago can name tabs that no longer exist. Views the user has placed keep
 * their relative order; anything new (a freshly installed plugin) lands at the
 * end, the way a new browser extension does, rather than jumping into the
 * middle of a bar the user has already arranged.
 */
export function orderAgentViews(
  availableViews: readonly string[],
  layout: AgentTabLayout,
): string[] {
  const available = new Set(availableViews);
  const placed = layout.order.filter((view) => available.has(view));
  const placedSet = new Set(placed);
  return [...placed, ...availableViews.filter((view) => !placedSet.has(view))];
}

/**
 * Drop unavailable ids and ensure at least one available view stays pinned.
 * A corrupt or stale pref that hid every tab would leave an empty rail.
 */
export function clampHiddenViews(
  availableViews: readonly string[],
  hidden: readonly string[],
): string[] {
  const available = new Set(availableViews);
  const next = unique(hidden.filter((view) => available.has(view)));
  if (availableViews.length === 0) return next;
  const pinned = availableViews.filter((view) => !next.includes(view));
  if (pinned.length > 0) return next;
  const keep = availableViews[0];
  return next.filter((view) => view !== keep);
}

/** The hidden views that are actually available on this agent (clamped). */
export function hiddenAgentViews(
  availableViews: readonly string[],
  layout: AgentTabLayout,
): string[] {
  return clampHiddenViews(availableViews, layout.hidden);
}

/** Available views that are still on the rail, in order. */
export function pinnedAgentViews(
  orderedViews: readonly string[],
  hidden: readonly string[],
): string[] {
  const hiddenSet = new Set(hidden);
  return orderedViews.filter((view) => !hiddenSet.has(view));
}

/**
 * When unpinning `leaving`, pick the nearest remaining pinned tab: prefer the
 * previous slot in order, otherwise the next.
 */
export function nearestPinnedFallback(
  orderedViews: readonly string[],
  hidden: readonly string[],
  leaving: string,
): string | undefined {
  const hiddenSet = new Set([...hidden, leaving]);
  const idx = orderedViews.indexOf(leaving);
  for (let i = idx - 1; i >= 0; i--) {
    const view = orderedViews[i];
    if (view && !hiddenSet.has(view)) return view;
  }
  for (let i = idx + 1; i < orderedViews.length; i++) {
    const view = orderedViews[i];
    if (view && !hiddenSet.has(view)) return view;
  }
  return undefined;
}

/** Whether there is anything for "Reset tab layout" to undo. */
export function isAgentTabLayoutCustomized(layout: AgentTabLayout): boolean {
  return layout.order.length > 0 || layout.hidden.length > 0;
}

/**
 * Merge a change scoped to the currently available views back into the stored
 * layout. Only available views can be dragged or toggled, so entries for views
 * that are off right now (an uninstalled plugin, a stopped dev server) are
 * carried over untouched instead of being dropped on the next drag.
 */
function mergeLayout(
  layout: AgentTabLayout,
  availableViews: readonly string[],
  change: Partial<AgentTabLayout>,
): AgentTabLayout {
  const available = new Set(availableViews);
  const merged = { ...layout };
  if (change.order) {
    merged.order = [
      ...change.order,
      ...layout.order.filter((view) => !change.order!.includes(view)),
    ];
  }
  if (change.hidden) {
    const carried = layout.hidden.filter((view) => !available.has(view));
    merged.hidden = [...clampHiddenViews(availableViews, change.hidden), ...carried];
  }
  return merged;
}

/**
 * The user's agent view tab layout, persisted per browser (same mechanism as
 * the session sort order and sidebar width) and reactive across components and
 * tabs via `useLocalStorageState`.
 *
 * Returns the available views in the user's order plus a ready-made
 * {@link SegmentedTabsCustomization} to hand to the tab strip.
 */
export function useAgentTabLayout(availableViews: string[]): {
  orderedViews: string[];
  customization: SegmentedTabsCustomization;
} {
  const [raw, setRaw] = useLocalStorageState<unknown>(STORAGE_KEY, DEFAULT_AGENT_TAB_LAYOUT);
  const layout = useMemo(() => parseAgentTabLayout(raw), [raw]);

  const orderedViews = useMemo(
    () => orderAgentViews(availableViews, layout),
    [availableViews, layout],
  );
  const hidden = useMemo(() => hiddenAgentViews(availableViews, layout), [availableViews, layout]);

  const update = useCallback(
    (change: Partial<AgentTabLayout>) =>
      // Read through `parseAgentTabLayout` rather than the memoized `layout` so
      // a write started from a stale render still merges onto what is actually
      // stored (another tab may have written in between).
      setRaw((prev: unknown) => mergeLayout(parseAgentTabLayout(prev), availableViews, change)),
    [setRaw, availableViews],
  );

  const onOrderChange = useCallback((order: string[]) => update({ order }), [update]);
  const onHiddenChange = useCallback(
    (hiddenViews: string[]) => update({ hidden: hiddenViews }),
    [update],
  );
  const onReset = useCallback(() => setRaw(DEFAULT_AGENT_TAB_LAYOUT), [setRaw]);

  const customized = isAgentTabLayoutCustomized(layout);
  const customization = useMemo<SegmentedTabsCustomization>(
    () => ({
      hidden,
      onOrderChange,
      onHiddenChange,
      // Only offer a reset when there is something to reset.
      onReset: customized ? onReset : undefined,
    }),
    [hidden, onOrderChange, onHiddenChange, onReset, customized],
  );

  return { orderedViews, customization };
}
